/**
 * App shell contracts (WP0-SHELL). Imported as `@velora/contracts/shell`.
 *
 * The web shell boots from `GET /v1/shell/bootstrap` (who am I, which
 * offices may I see, what may I do, which add-ons are on per office, my
 * remembered preferences), keeps per-user preferences with
 * `PUT /v1/shell/preferences`, shows connector freshness from
 * `GET /v1/shell/freshness`, searches patients for the top bar and palette
 * with `GET /v1/shell/patients`, and renders the Home dashboard from
 * `GET /v1/shell/home`.
 *
 * Multi-office (ADR-0012): every per-office payload carries one row per
 * office in scope plus a server-computed combined total. Ratios are computed
 * from summed parts, never averaged.
 */
import { z } from 'zod';
import { MetricGapSchema } from './analytics.js';
import { EntitlementStateSchema } from './core.js';
import { MarketplaceSettingsSchema } from './marketplace.js';
import { CallerMatchSchema } from './receptionist.js';
import {
  APPEARANCE_MODES,
  SkinDefaultsSchema,
  SkinIdSchema,
  SkinOverridesSchema,
} from './skins.js';

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/* ------------------------------------------------------------------------ */
/* Offices                                                                   */
/* ------------------------------------------------------------------------ */

export const PmsSystemSchema = z.enum(['opendental', 'dentrix', 'eaglesoft']);
export type PmsSystem = z.infer<typeof PmsSystemSchema>;

/** An office the user may access, as the office selector shows it. */
export const ShellOfficeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  /**
   * Stable 0-based colour slot from `location.color_token` (`office-N` is
   * slot N-1). The same office has the same colour in every chart.
   */
  colorIndex: z.number().int().min(0).max(11),
  timezone: z.string(),
  /** PMS behind this office's active link, `null` when none is linked. */
  pmsSystem: PmsSystemSchema.nullable(),
});
export type ShellOffice = z.infer<typeof ShellOfficeSchema>;

/* ------------------------------------------------------------------------ */
/* Preferences (per user, per practice)                                      */
/* ------------------------------------------------------------------------ */

export const SHELL_DENSITIES = ['comfortable', 'compact', 'classic'] as const;

/**
 * What the shell remembers for a user across devices. Every field is
 * optional; a missing field means "use the default". Device-only settings
 * (performance mode) stay in local storage.
 */
/**
 * A user's own layout of one classic toolbar (Dentrix right-click >
 * Customize Toolbar, requests-dentrix I-13): action ids hidden, and an
 * optional order of the ids that remain. Unknown ids are ignored.
 */
export const ToolbarLayoutSchema = z.object({
  hidden: z.array(z.string().min(1).max(80)).max(200),
  order: z.array(z.string().min(1).max(80)).max(200).optional(),
});
export type ToolbarLayout = z.infer<typeof ToolbarLayoutSchema>;

/** Per mode, per toolbar (`<module id>` or `default`). */
export const ToolbarLayoutsSchema = z.partialRecord(
  SkinIdSchema,
  z.record(z.string().min(1).max(64), ToolbarLayoutSchema),
);
export type ToolbarLayouts = z.infer<typeof ToolbarLayoutsSchema>;

/**
 * The user's own Velora Modern side nav (apps/web layout/sidenav): pinned
 * item ids in order (missing = the default pins), items hidden from the
 * sidebar (still in "All modules"), collapsed categories, and whether the
 * "Not set up" area is open. Ids are `<module>:<route>`; unknown ids are
 * ignored, so a removed module never breaks the layout.
 */
export const NavLayoutSchema = z.object({
  pins: z.array(z.string().min(1).max(80)).max(60).optional(),
  hidden: z.array(z.string().min(1).max(80)).max(200).optional(),
  collapsed: z.array(z.string().min(1).max(40)).max(20).optional(),
  notSetUpOpen: z.boolean().optional(),
});
export type NavLayout = z.infer<typeof NavLayoutSchema>;

/** Who Home is laid out for (apps/web home/dashboard/model.ts). */
export const DASHBOARD_ROLES = [
  'owner',
  'front_desk',
  'hygienist',
  'dentist',
  'billing',
  'treatment_coordinator',
] as const;
export type DashboardRole = (typeof DASHBOARD_ROLES)[number];

/**
 * The user's own Home: the cards they pinned (in order; missing = their
 * role's default) and, optionally, which role's layout they start from.
 * Unknown card ids are ignored, so a removed card never breaks Home.
 */
export const HomeLayoutSchema = z.object({
  pins: z.array(z.string().min(1).max(40)).max(60).optional(),
  role: z.enum(DASHBOARD_ROLES).optional(),
  /**
   * Home prompts this user snoozed: prompt key to when it comes back and the
   * count it had (a higher count brings it back early: something new).
   */
  snoozed: z
    .record(
      z.string().min(1).max(60),
      z.object({ until: z.iso.datetime({ offset: true }), n: z.number().int().min(0) }),
    )
    .refine((o) => Object.keys(o).length <= 40, 'at most 40 snoozed prompts')
    .optional(),
  /** Pop up a notice when a new urgent prompt appears (default on). */
  toastUrgent: z.boolean().optional(),
  /** Home prompts strip opened to its detailed rows (default: collapsed chips). */
  promptsExpanded: z.boolean().optional(),
});
export type HomeLayout = z.infer<typeof HomeLayoutSchema>;

/** What fills a schedule card: the provider's colour or the appointment type's. */
export const SCHEDULE_COLOR_BY = ['provider', 'type'] as const;
export type ScheduleColorBy = (typeof SCHEDULE_COLOR_BY)[number];

/**
 * A table's column sort, remembered per user (owner feedback 2026-10-28:
 * "make the sort persist per user"). Keyed by a stable table id such as
 * `ledger.family`, `chart.procedures`, `claims.table`, `payments.list`.
 */
export const TableSortSchema = z.object({
  key: z.string().min(1).max(32),
  dir: z.enum(['asc', 'desc']),
});
export type TableSort = z.infer<typeof TableSortSchema>;
export const TableSortsSchema = z
  .record(z.string().min(1).max(48), TableSortSchema)
  .refine((v) => Object.keys(v).length <= 40, 'Too many remembered table sorts');
export type TableSorts = z.infer<typeof TableSortsSchema>;

export const ShellPreferencesSchema = z.object({
  /** Office selector value: `all` or an office id (ADR-0012). */
  officeScope: z.string().max(64).optional(),
  theme: z.enum(['dark', 'light']).optional(),
  density: z.enum(SHELL_DENSITIES).optional(),
  navCollapsed: z.boolean().optional(),
  /**
   * Interface mode (skin, docs/themes/README.md). Missing: the practice
   * default, else the office's PMS (`resolveSkin`). Per employee, so one
   * practice can run several modes at once.
   */
  skin: SkinIdSchema.optional(),
  /** Single-layer overrides (Settings > Appearance > Advanced). */
  skinOverrides: SkinOverridesSchema.optional(),
  /**
   * Light or dark per non-Velora mode (Velora Modern keeps using `theme`).
   * Missing: `SKIN_DEFAULT_MODE`.
   */
  skinModes: z.partialRecord(SkinIdSchema, z.enum(APPEARANCE_MODES)).optional(),
  /** The first-login "which PMS do you know?" step was answered or skipped. */
  skinOnboarded: z.boolean().optional(),
  /** Customized classic toolbars (`ClassicChrome.customizableToolbar`). */
  toolbarLayouts: ToolbarLayoutsSchema.optional(),
  /** Side-nav pins, hidden items and collapsed categories (Velora Modern). */
  navLayout: NavLayoutSchema.optional(),
  /** Home cards pinned and the role layout Home starts from. */
  homeLayout: HomeLayoutSchema.optional(),
  /** Velora Modern schedule: "Colour appointments by". Missing: provider. */
  scheduleColorBy: z.enum(SCHEDULE_COLOR_BY).optional(),
  /**
   * Velora Modern schedule: "My schedule" (only the signed-in provider's
   * columns). Missing: on for dentists and hygienists, off for everyone else.
   */
  scheduleMine: z.boolean().optional(),
  /** Column sorts of the big tables (ledger, procedures, claims, payments), per user. */
  tableSorts: TableSortsSchema.optional(),
});
export type ShellPreferences = z.infer<typeof ShellPreferencesSchema>;

/** `PUT /v1/shell/preferences` body: fields to change; `null` clears one. */
export const ShellPreferencesPatchSchema = z.object({
  officeScope: z.string().max(64).nullable().optional(),
  theme: z.enum(['dark', 'light']).nullable().optional(),
  density: z.enum(SHELL_DENSITIES).nullable().optional(),
  navCollapsed: z.boolean().nullable().optional(),
  skin: SkinIdSchema.nullable().optional(),
  skinOverrides: SkinOverridesSchema.nullable().optional(),
  skinModes: z.partialRecord(SkinIdSchema, z.enum(APPEARANCE_MODES)).nullable().optional(),
  skinOnboarded: z.boolean().nullable().optional(),
  toolbarLayouts: ToolbarLayoutsSchema.nullable().optional(),
  navLayout: NavLayoutSchema.nullable().optional(),
  homeLayout: HomeLayoutSchema.nullable().optional(),
  scheduleColorBy: z.enum(SCHEDULE_COLOR_BY).nullable().optional(),
  scheduleMine: z.boolean().nullable().optional(),
  tableSorts: TableSortsSchema.nullable().optional(),
});
export type ShellPreferencesPatch = z.infer<typeof ShellPreferencesPatchSchema>;

/* ------------------------------------------------------------------------ */
/* Bootstrap                                                                 */
/* ------------------------------------------------------------------------ */

export const ShellBootstrapSchema = z.object({
  user: z.object({
    id: z.uuid(),
    displayName: z.string(),
    /** Set while C2G support impersonates this user. */
    impersonatedBy: z.string().nullable(),
    /**
     * The provider record linked to this user (dentist role test: "Me" on
     * the schedule, Rx prescriber, "Next for me"); null for staff who are
     * not providers. Absent from older servers.
     */
    provider: z
      .object({
        id: z.uuid(),
        name: z.string(),
        type: z.enum(['dentist', 'hygienist', 'assistant', 'other']),
      })
      .nullable()
      .optional(),
  }),
  practice: z.object({ id: z.uuid(), name: z.string(), timezone: z.string() }),
  /** Offices granted by `user_location_access`, active only, in display order. */
  offices: z.array(ShellOfficeSchema),
  defaultLocationId: z.uuid().nullable(),
  /** Practice-wide permission keys. */
  permissions: z.array(z.string()),
  /** Per-office overrides: when an office is listed, its set replaces `permissions` there. */
  locationPermissions: z.record(z.string(), z.array(z.string())),
  /** Modules the person's roles hide (`ROLE_MODULES` in identity): nav and page sections leave them out. */
  hiddenModules: z.array(z.string()).optional(),
  /**
   * Effective Marketplace state per feature and office:
   * `entitlements[featureKey][locationId]`. Features the API knows about
   * (module manifests) are always listed, so a missing office means `none`.
   */
  entitlements: z.record(z.string(), z.record(z.string(), EntitlementStateSchema)),
  /** Features where at least one office may still start a trial. */
  trialsAvailable: z.array(z.string()),
  preferences: ShellPreferencesSchema,
  /** Practice-level skin settings (default mode, imaging system). */
  skinDefaults: SkinDefaultsSchema.optional(),
  /** Practice Marketplace settings ("Hide modules we don't use"). */
  marketplace: MarketplaceSettingsSchema.optional(),
  serverTime: z.string(),
});
export type ShellBootstrap = z.infer<typeof ShellBootstrapSchema>;

/* ------------------------------------------------------------------------ */
/* Integrations (side nav "Not set up")                                      */
/* ------------------------------------------------------------------------ */

/** Outside services a nav item depends on (`GET /v1/shell/integrations`). */
export const INTEGRATION_KEYS = [
  'online_booking',
  'phones',
  'texting',
  'reviews',
  'eligibility',
  'receptionist',
  'imaging_ai',
  // Patient Map: the practice's own geocoder (Analytics hides the map without it).
  'geocoder',
] as const;
export const IntegrationKeySchema = z.enum(INTEGRATION_KEYS);
export type IntegrationKey = z.infer<typeof IntegrationKeySchema>;

/**
 * `connected`: linked at one or more offices in scope (it may still be
 * waiting for its first data, see `detail`). `not_connected`: no office in
 * scope has it linked; simulators and mock sources count as not connected.
 * `unknown`: the check failed, so the shell keeps the item where it was.
 */
export const IntegrationStateSchema = z.enum(['connected', 'not_connected', 'unknown']);
export type IntegrationState = z.infer<typeof IntegrationStateSchema>;

export const IntegrationStatusSchema = z.object({
  key: IntegrationKeySchema,
  state: IntegrationStateSchema,
  /** Offices in scope where it is connected, of `officesTotal`. */
  officesConnected: z.number().int().nonnegative(),
  officesTotal: z.number().int().nonnegative(),
  /** One plain sentence ("No Google review source is connected."). */
  detail: z.string().nullable(),
  /** Where an admin sets it up. */
  setupHref: z.string(),
});
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;

export const ShellIntegrationsSchema = z.object({
  integrations: z.array(IntegrationStatusSchema),
  checkedAt: z.string(),
});
export type ShellIntegrations = z.infer<typeof ShellIntegrationsSchema>;

/* ------------------------------------------------------------------------ */
/* Freshness                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * One synced entity's freshness against its target (V08, the one freshness
 * rule from the V06 contract): `ok` within the target, `behind` past it,
 * `stalled` past twice the target, `missing` never synced.
 */
export const FreshnessSourceSchema = z.object({
  entity: z.string(),
  lastSuccessAt: z.string().nullable(),
  ageSec: z.number().int().nullable(),
  /** Target age (the entity's poll interval) in seconds. */
  sloSec: z.number().int(),
  state: z.enum(['ok', 'behind', 'stalled', 'missing']),
});
export type FreshnessSource = z.infer<typeof FreshnessSourceSchema>;

export const OfficeFreshnessSchema = z.object({
  locationId: z.uuid(),
  /** `null` when the office has no PMS link (Velora is the system of record or not set up). */
  pmsSystem: PmsSystemSchema.nullable(),
  adapter: z.enum(['mock', 'pulsesync-v1', 'pulsesync-v2']).nullable(),
  /**
   * The core feed's last successful read: the older of appointments and
   * patients (or the connector heartbeat once it is past 15 minutes). Drives
   * the top-bar badge and the "not updating" alert; slower entities never do.
   */
  lastSyncedAt: z.string().nullable(),
  writesPaused: z.boolean(),
  /**
   * KPI-source freshness (HA-08, ADM-04, PT-07): the sources the KPIs are
   * computed from (procedures for production, appointments for the
   * schedule), each against its own target. Everywhere that says how old the
   * numbers are uses these, never a minimum over every entity.
   */
  sources: z.array(FreshnessSourceSchema).optional(),
  /** Oldest last-success of the KPI sources; `null` without a link or a sync. */
  kpiSyncedAt: z.string().nullable().optional(),
  /** The KPI source furthest past its target, if any is past it. */
  stale: z.object({ entity: z.string(), ageSec: z.number().int() }).nullable().optional(),
  /**
   * Appointments or patients behind their target: alert-worthy. PulseSync
   * offices: past the warning level of the connector-interval rule.
   */
  degraded: z.boolean().optional(),
  /** Chip text: "Synced 4m ago", or the stale source ("procedures 1h 50m behind"). */
  label: z.string().optional(),
  /**
   * Other entities (reference data, daily readers) past twice their OWN poll
   * interval. Informational only: never part of the badge or the alert.
   */
  lagging: z.array(FreshnessSourceSchema).optional(),
  /**
   * PulseSync offices: the connector's sync interval (s), what PulseSync
   * reports or 5 minutes. New PMS data lands once per cycle.
   */
  intervalSec: z.number().int().positive().optional(),
  /**
   * PulseSync offices: `lastSyncedAt` judged against `intervalSec`, the one
   * rule of the Sync status page, the top-bar chip and the Home alert:
   * warning past 2x the interval plus 2 minutes, error past 4x.
   */
  syncLevel: z.enum(['ok', 'warning', 'error', 'unknown']).optional(),
});
export type OfficeFreshness = z.infer<typeof OfficeFreshnessSchema>;

export const ShellFreshnessSchema = z.object({
  offices: z.array(OfficeFreshnessSchema),
  checkedAt: z.string(),
});
export type ShellFreshness = z.infer<typeof ShellFreshnessSchema>;

/* ------------------------------------------------------------------------ */
/* Patient search (top bar, palette)                                         */
/* ------------------------------------------------------------------------ */

export const PatientSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(80),
  limit: z.coerce.number().int().min(1).max(25).default(8),
});

export const PatientSearchHitSchema = z.object({
  id: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  chartNumber: z.string().nullable(),
  birthdate: IsoDateSchema.nullable(),
  homeLocationId: z.uuid().nullable(),
  status: z.string(),
  /**
   * The office this hit is listed under (the selected office, else the home
   * office, else the first office seen at). Absent from older servers.
   */
  locationId: z.uuid().nullable().optional(),
  /** Offices in the request's scope the patient belongs to (home first). */
  officeIds: z.array(z.uuid()).optional(),
});
export type PatientSearchHit = z.infer<typeof PatientSearchHitSchema>;

export const PatientSearchResultSchema = z.object({ hits: z.array(PatientSearchHitSchema) });
export type PatientSearchResult = z.infer<typeof PatientSearchResultSchema>;

/* ------------------------------------------------------------------------ */
/* Select Patient (the familiar modes' dialogs: Dentrix Select Patient,      */
/* Open Dental Select Patient, Eaglesoft Person List)                        */
/* ------------------------------------------------------------------------ */

/**
 * What one Select Patient box searches. `auto` reads the text like the top
 * bar does (name, "Last, First", chart number, birthdate). `last` and
 * `first` accept "Last, First" and "First, Last"; `phone` matches any of the
 * patient's phones by digits; `ssn` matches the last four digits and needs
 * `patient.view_ssn`; `pmsId` is the PMS's own patient number (Open Dental
 * PatNum) from the sync mapping.
 */
export const SELECT_PATIENT_FIELDS = [
  'auto',
  'last',
  'first',
  'preferred',
  'phone',
  'chart',
  'dob',
  'address',
  'city',
  'state',
  'ssn',
  'subscriberId',
  'email',
  'pmsId',
] as const;
export const SelectPatientFieldSchema = z.enum(SELECT_PATIENT_FIELDS);
export type SelectPatientField = z.infer<typeof SelectPatientFieldSchema>;

export const SelectPatientCriterionSchema = z.object({
  field: SelectPatientFieldSchema,
  q: z.string().trim().min(1).max(80),
});
export type SelectPatientCriterion = z.infer<typeof SelectPatientCriterionSchema>;

/**
 * `POST /v1/shell/patients/select`. The query travels in the body so it
 * never reaches a URL or an access log. Criteria are combined with AND
 * (Open Dental's "enter values in multiple boxes"). No criteria returns the
 * user's recently selected patients (`recent: true`) instead of a "Get All"
 * scan of every patient (PT-19 / OD-33: that query took 17 s on open).
 */
export const SelectPatientQuerySchema = z
  .object({
    criteria: z.array(SelectPatientCriterionSchema).max(8),
    /** Only patients who are the guarantor (head of household) of their family. */
    guarantorsOnly: z.boolean().default(false),
    /** Inactive patients and non-patients. */
    includeInactive: z.boolean().default(true),
    /** Archived and deceased patients. */
    includeArchived: z.boolean().default(true),
    /** Rows returned; at most 200 (PT-19). */
    limit: z.number().int().min(1).max(200).default(40),
  })
  .strict();
export type SelectPatientQueryInput = z.input<typeof SelectPatientQuerySchema>;
export type SelectPatientQuery = z.output<typeof SelectPatientQuerySchema>;

/** One row of a Select Patient grid, with what the PMS dialogs show. */
export const SelectPatientRowSchema = PatientSearchHitSchema.extend({
  middleInitial: z.string().nullable(),
  /** Whole years on the practice's today; `null` without a birthdate. */
  age: z.number().int().min(0).nullable(),
  /** The PMS's own patient number (Open Dental PatNum) from the sync mapping. */
  pmsId: z.string().nullable(),
  pmsSystem: PmsSystemSchema.nullable(),
  /** Head of household (the family's guarantor), "Last, First". */
  headOfHouse: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** This patient is the guarantor of their family. */
  isGuarantor: z.boolean(),
  homePhone: z.string().nullable(),
  workPhone: z.string().nullable(),
  cellPhone: z.string().nullable(),
  email: z.string().nullable(),
  addressLine1: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** Practice-local date of the last completed visit. */
  lastVisitDate: IsoDateSchema.nullable(),
  /** Last four SSN digits; `null` unless the user has `patient.view_ssn`. */
  ssnLast4: z
    .string()
    .regex(/^\d{4}$/)
    .nullable(),
});
export type SelectPatientRow = z.infer<typeof SelectPatientRowSchema>;

export const SelectPatientResultSchema = z.object({
  hits: z.array(SelectPatientRowSchema),
  /** The user may see and search SSN last four (`patient.view_ssn`). */
  ssnVisible: z.boolean(),
  /** True when no criteria were given and `hits` is the user's recent list. */
  recent: z.boolean().optional(),
});
export type SelectPatientResult = z.infer<typeof SelectPatientResultSchema>;

/** Patients kept in a user's server-side recent list (per user, per practice). */
export const RECENT_PATIENTS_MAX = 20;

/** `POST /v1/shell/patients/recent`: the user selected (opened) this patient. */
export const RecentPatientMarkSchema = z.object({ patientId: z.uuid() }).strict();
export type RecentPatientMark = z.infer<typeof RecentPatientMarkSchema>;
export const RecentPatientMarkResultSchema = z.object({ ok: z.literal(true) });

/** `GET /v1/shell/patients/recent`: newest first, same rows as Select Patient. */
export const RecentPatientsSchema = SelectPatientResultSchema;
export type RecentPatients = SelectPatientResult;

/* ------------------------------------------------------------------------ */
/* Home dashboard                                                            */
/* ------------------------------------------------------------------------ */

/** Additive metrics: counts and integer cents. Safe to sum across offices. */
export const HOME_METRICS = [
  'productionTodayCents',
  'productionMtdCents',
  'collectionsMtdCents',
  'scheduledToday',
  'completedToday',
  'missedToday',
  'missedMtd',
  'visitsMtd',
  'newPatientsMtd',
  'bookedMinutesToday',
] as const;
export type HomeMetric = (typeof HOME_METRICS)[number];

/** Metrics that are money: `null` for users without `money.view`. */
export const HOME_MONEY_METRICS = [
  'productionTodayCents',
  'productionMtdCents',
  'collectionsMtdCents',
] as const satisfies readonly HomeMetric[];

const metricShape = Object.fromEntries(
  HOME_METRICS.map((k) => [k, z.number().int().nullable()]),
) as Record<HomeMetric, z.ZodNullable<z.ZodNumber>>;

export const HomeMetricsSchema = z.object({
  ...metricShape,
  /** Collections / production, month to date, from summed parts. `null` without money.view or production. */
  collectionRateMtd: z.number().nullable(),
  /** Missed / appointments in the month (the dictionary broken_appointment_rate). */
  missedRateMtd: z.number().nullable(),
  /**
   * Values the office's PMS link cannot supply (V08), keyed by metric name
   * (`collectionsMtdCents`, `collectionRateMtd`, `newPatientsMtd`): shown as
   * "Not synced from <PMS>", never as $0 / 0% / 0. On the total row a key is
   * present only when no office in view can supply it.
   */
  unavailable: z.record(z.string(), MetricGapSchema).optional(),
  /** Total row: per metric, offices left out of the total because they cannot supply it. */
  unavailableOffices: z.record(z.string(), z.number().int()).optional(),
});
export type HomeMetrics = z.infer<typeof HomeMetricsSchema>;

export const HomeOfficeRowSchema = HomeMetricsSchema.extend({ locationId: z.uuid() });
export type HomeOfficeRow = z.infer<typeof HomeOfficeRowSchema>;

export const HomeSeriesSchema = z.object({
  /** Production per day (cents), `null` without money.view. */
  productionCents: z.array(z.number().int()).nullable(),
  /** Completed visits per day. */
  visits: z.array(z.number().int()),
});
export type HomeSeries = z.infer<typeof HomeSeriesSchema>;

/** Home's day picker reach around today: the past 30 days and the next 14. */
export const HOME_DAY_PAST_DAYS = 30;
export const HOME_DAY_FUTURE_DAYS = 14;

/**
 * One day's schedule counts for Home's day picker, from the same per-day
 * appointment counts as the Today strip (office_daily_stats definitions):
 * booked = scheduled + completed, missed = broken + no-show.
 */
export const HomeSelectedDaySchema = z.object({
  date: IsoDateSchema,
  booked: z.number().int(),
  completed: z.number().int(),
  missed: z.number().int(),
  bookedMinutes: z.number().int(),
});
export type HomeSelectedDay = z.infer<typeof HomeSelectedDaySchema>;

export const HomeDashboardSchema = z.object({
  /** Practice-local date the numbers are for. */
  date: IsoDateSchema,
  monthStart: IsoDateSchema,
  /** Offices in scope, in display order. */
  locationIds: z.array(z.uuid()),
  perOffice: z.array(HomeOfficeRowSchema),
  total: HomeMetricsSchema,
  /** True when money figures were withheld (no `money.view`). */
  moneyHidden: z.boolean(),
  series: z.object({
    /** Day labels, oldest first, ending at `date`. */
    days: z.array(IsoDateSchema),
    perOffice: z.array(HomeSeriesSchema.extend({ locationId: z.uuid() })),
    total: HomeSeriesSchema,
  }),
  /**
   * Unacknowledged fee outliers inside this month's production (V08 outlier
   * guard): staff check them in Data hygiene. `cents` is `null` without money.view.
   */
  feeOutliers: z.object({ count: z.number().int(), cents: z.number().int().nullable() }).optional(),
  /** Where the month-to-date numbers came from: the live dataset or the materialised rollup. */
  source: z.enum(['live', 'rollup', 'views']).optional(),
  /** When the numbers were computed (rollup time, or now). */
  computedAt: z.string().optional(),
  /**
   * True while the offices' analytics dataset is still loading (the first
   * view after an API start) and no stored rollup could stand in: the
   * dictionary tiles are missing (`source: 'views'`); ask again shortly.
   */
  warming: z.boolean().optional(),
  /** The schedule on the day asked for with `day` (Home's day picker); absent without `day`. */
  selectedDay: HomeSelectedDaySchema.optional(),
});
export type HomeDashboard = z.infer<typeof HomeDashboardSchema>;

export const HomeDashboardQuerySchema = z.object({
  /** Practice-local date to report; defaults to today. */
  date: IsoDateSchema.optional(),
  /**
   * Home's day picker: the schedule counts for this day come back in
   * `selectedDay` (the month-to-date numbers stay on `date`). Within
   * HOME_DAY_PAST_DAYS before and HOME_DAY_FUTURE_DAYS after `date`.
   */
  day: IsoDateSchema.optional(),
  /** Trailing days in the series, including `date`. */
  days: z.coerce.number().int().min(7).max(92).default(30),
});

/** Longest window the Home daily production chart may ask for (days, inclusive). */
export const HOME_DAILY_MAX_DAYS = 92;

export const HomeDailyProductionQuerySchema = z.object({
  /** First practice-local day, inclusive. */
  from: IsoDateSchema,
  /** Last practice-local day, inclusive (at most HOME_DAILY_MAX_DAYS after `from`). */
  to: IsoDateSchema,
});
export type HomeDailyProductionQuery = z.infer<typeof HomeDailyProductionQuerySchema>;

/**
 * Daily production for a date window (Home "Daily production" card). Each
 * day's production is the dictionary's gross production (`production_gross`:
 * fees of completed procedures by completion date) and its visits are the
 * dictionary's visits, so the days add up to what Analytics shows for the
 * same offices and period.
 */
export const HomeDailyProductionSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  /** The practice-local date today (days after it have not happened yet). */
  today: IsoDateSchema,
  /** Offices in scope, in display order. */
  locationIds: z.array(z.uuid()),
  /** True when production was withheld (no `money.view` at every office in scope). */
  moneyHidden: z.boolean(),
  days: z.array(
    z.object({
      date: IsoDateSchema,
      /** Gross production (cents), `null` without money.view. */
      productionCents: z.number().int().nullable(),
      /** Completed visits (one patient, one office, one date). */
      visits: z.number().int(),
    }),
  ),
});
export type HomeDailyProduction = z.infer<typeof HomeDailyProductionSchema>;

/* ------------------------------------------------------------------------ */
/* Helpers shared by API and web                                             */
/* ------------------------------------------------------------------------ */

/** `office-3` -> 2. Unknown tokens fall back to slot 0. */
export function colorIndexFromToken(token: string | null | undefined): number {
  const m = /^office-(\d{1,2})$/.exec(token ?? '');
  const n = m ? Number(m[1]) : 1;
  return n >= 1 && n <= 12 ? n - 1 : 0;
}

/** Ratio of summed parts; `null` when either side is unknown or the denominator is 0. */
export function ratio(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null;
  return numerator / denominator;
}

/* ------------------------------------------------------------------------ */
/* Home prompts (GET /v1/shell/prompts)                                      */
/* ------------------------------------------------------------------------ */

/**
 * The "warn us" prompts on Home (owner 2026-10-20: "call analytics and
 * missed calls and leads and voicemails ... should be warning us with
 * prompts on the home dashboard just like it does in our Atlas app").
 * Counts and the oldest time only: no names, numbers or messages (PHI stays
 * on the page each prompt opens, which audits its own read). Each prompt is
 * computed only over the offices where the user holds its permission (and
 * the office has the module), so a count never includes what the click-
 * through could not show.
 */
export const HOME_PROMPT_KEYS = [
  'missed_calls',
  'voicemails',
  'leads',
  'texts',
  'missed_call_replies',
  'portal_messages',
  'ai_calls',
  'after_hours_ai',
  'answer_rate',
  'booking_requests',
  'portal_requests',
] as const;
export const HomePromptKeySchema = z.enum(HOME_PROMPT_KEYS);
export type HomePromptKey = z.infer<typeof HomePromptKeySchema>;

/**
 * Missed calls, voicemails and AI follow-ups count only the last this many
 * hours (Atlas's "last 72 hours"), and never a call from before the office
 * went live on Velora phones (backfilled 3CX history).
 */
export const WAITING_WINDOW_HOURS = 72;

/** `urgent` (rose): someone has waited too long or something is flagged urgent. `attention` (orange). */
export const HomePromptSeveritySchema = z.enum(['urgent', 'attention']);
export type HomePromptSeverity = z.infer<typeof HomePromptSeveritySchema>;

export const HomePromptSchema = z.object({
  key: HomePromptKeySchema,
  severity: HomePromptSeveritySchema,
  /** How many are waiting (for `answer_rate`: inbound calls not answered today). */
  count: z.number().int().min(0),
  /** Of those, how many are urgent on their own (flagged urgent, past due, over the SLA). */
  urgentCount: z.number().int().min(0),
  /** When the oldest one started waiting. */
  oldestAt: z.iso.datetime({ offset: true }).nullable(),
  /** A short, PHI-free line (sources, rates). */
  detail: z.string().max(200).nullable(),
  /**
   * The count per office (offices with none left out), largest first: the
   * "McLaren 5 · Signature 4" split on Home. Empty when the source has no
   * per-office answer.
   */
  byOffice: z.array(z.object({ locationId: z.uuid(), count: z.number().int().min(0) })).default([]),
  /** The page that works the list, plus its search params. */
  href: z.string(),
  search: z.record(z.string(), z.string()),
  /**
   * Counted from (the phone prompts): the start of the WAITING_WINDOW_HOURS
   * window, or the office's Velora phones go-live when that is later. Null
   * or absent: the source has no window.
   */
  since: z.iso.datetime({ offset: true }).nullable().optional(),
});
export type HomePrompt = z.infer<typeof HomePromptSchema>;

export const HomePromptsSchema = z.object({
  generatedAt: z.iso.datetime({ offset: true }),
  /** The phone prompts' window in hours (WAITING_WINDOW_HOURS). */
  windowHours: z.number().int().positive().optional(),
  /** Only prompts with something waiting. */
  prompts: z.array(HomePromptSchema),
  /** Prompts the user may see whose source did not load (never shown as zero). */
  unavailable: z.array(HomePromptKeySchema),
});
export type HomePrompts = z.infer<typeof HomePromptsSchema>;

/**
 * The prompts whose chip opens the exact list it counts on Home
 * (`GET /v1/shell/prompts/:key/items`). Texts open Messages filtered
 * (`?filter=unanswered|missed_call_replies`) instead.
 */
export const WAITING_LIST_KEYS = [
  'missed_calls',
  'voicemails',
  'ai_calls',
  'after_hours_ai',
] as const;
export const WaitingListKeySchema = z.enum(WAITING_LIST_KEYS);
export type WaitingListKey = z.infer<typeof WaitingListKeySchema>;

/** One thing a Home chip counted. PHI (names, numbers, the AI's notes). */
export const WaitingItemSchema = z.object({
  /** Call id (missed calls: the newest miss), or the receptionist call id (after_hours_ai). */
  id: z.uuid(),
  locationId: z.uuid(),
  /** When it started waiting (the oldest counted miss, the call, the voicemail). */
  at: z.iso.datetime({ offset: true }),
  phone: z.string().nullable(),
  /** Who called, from the number when nobody verified (staff-only). */
  caller: CallerMatchSchema,
  /** "2 missed calls · automatic text sent 10:06", "Flagged: needs follow-up". */
  title: z.string(),
  /** What the AI (call review or the AI receptionist) took down; null without patient.view. */
  takenDown: z.string().nullable(),
  /** What staff should do: "Call back", "Listen, then call back", the AI's follow-up items. */
  action: z.string(),
  /** Marks it handled, removing it from the count; null when it can only be cleared on its page. */
  done: z.object({ label: z.string() }).nullable(),
  /** The page that works this item. */
  open: z.object({ href: z.string(), search: z.record(z.string(), z.string()) }),
  urgent: z.boolean(),
});
export type WaitingItem = z.infer<typeof WaitingItemSchema>;

export const WaitingListSchema = z.object({
  key: WaitingListKeySchema,
  generatedAt: z.iso.datetime({ offset: true }),
  /** Same number as the chip (the list is the chip's own rows). */
  count: z.number().int().nonnegative(),
  items: z.array(WaitingItemSchema),
});
export type WaitingList = z.infer<typeof WaitingListSchema>;

export const WaitingDoneSchema = z.object({ note: z.string().trim().max(500).optional() }).strict();
export type WaitingDone = z.infer<typeof WaitingDoneSchema>;
